import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import type { Scene } from "@babylonjs/core/scene";
import { PickupSpriteGenerator } from "../assets/PickupSpriteGenerator";
import type { PickupSpawn } from "../generation/types";
import type { PlayerInventory } from "../player/PlayerInventory";
import type { MaterialFactory } from "../rendering/MaterialFactory";
import { gridToWorld } from "../rendering/worldCoordinates";
import type { GridMap } from "../generation/GridMap";
import type { AudioSystem, SoundId } from "./AudioSystem";

interface PickupEntity {
  readonly spawn: PickupSpawn;
  readonly mesh: Mesh;
  collected: boolean;
  phase: number;
  baseY: number;
}

export interface PickupResult {
  readonly kind: PickupSpawn["kind"];
  readonly message: string;
}

export class PickupSystem {
  private readonly pickups: PickupEntity[];
  private elapsed = 0;

  public constructor(
    spawns: readonly PickupSpawn[],
    scene: Scene,
    grid: GridMap,
    materials: MaterialFactory,
    private readonly inventory: PlayerInventory,
    private readonly audio: AudioSystem,
  ) {
    const sprites = new PickupSpriteGenerator();
    const pickupMaterials = new Map<
      PickupSpawn["kind"],
      ReturnType<MaterialFactory["createAtlasMaterial"]>
    >();
    this.pickups = spawns.map((spawn, index) => {
      const world = gridToWorld(spawn.cell, grid);
      const width = (spawn.kind === "shotgun" || spawn.kind === "rifle") ? 1.75 : 1.2;
      const height = (spawn.kind === "shotgun" || spawn.kind === "rifle") ? 1.1 : 1.25;
      const mesh = MeshBuilder.CreatePlane(
        `pickup-${spawn.id}`,
        { width, height, sideOrientation: Mesh.DOUBLESIDE },
        scene,
      );
      const baseY = height * 0.5 + 0.18;
      mesh.position.set(world.x, baseY, world.z);
      mesh.billboardMode = Mesh.BILLBOARDMODE_Y;
      let material = pickupMaterials.get(spawn.kind);
      if (!material) {
        material = materials.createAtlasMaterial(
          `pickup-material-${spawn.kind}`,
          sprites.get(spawn.kind),
        );
        pickupMaterials.set(spawn.kind, material);
      }
      mesh.material = material;
      mesh.isPickable = false;
      mesh.metadata = { pickupKind: spawn.kind };
      return { spawn, mesh, collected: false, phase: index * 0.7, baseY };
    });
  }

  public update(deltaSeconds: number, playerPosition: Vector3): PickupResult[] {
    this.elapsed += deltaSeconds;
    const results: PickupResult[] = [];
    for (const pickup of this.pickups) {
      if (pickup.collected) continue;
      pickup.mesh.position.y = pickup.baseY + Math.sin(this.elapsed * 2.4 + pickup.phase) * 0.08;
      if (Vector3.DistanceSquared(pickup.mesh.position, playerPosition) > 1.5) continue;
      if (!this.inventory.collect(pickup.spawn.kind)) continue;
      pickup.collected = true;
      pickup.mesh.setEnabled(false);
      this.audio.play(this.soundFor(pickup.spawn.kind));
      results.push({ kind: pickup.spawn.kind, message: pickupMessage(pickup.spawn.kind) });
    }
    return results;
  }

  public findPosition(
    kind: PickupSpawn["kind"],
  ): Readonly<{ position: Vector3; roomId: string }> | undefined {
    const pickup = this.pickups.find(
      (candidate) => candidate.spawn.kind === kind && !candidate.collected,
    );
    return pickup
      ? { position: pickup.mesh.position.clone(), roomId: pickup.spawn.roomId }
      : undefined;
  }

  private soundFor(kind: PickupSpawn["kind"]): SoundId {
    if (kind === "health" || kind === "armor") return "pickup_health";
    if (kind === "shotgun" || kind === "rifle") return "pickup_weapon";
    return "pickup_ammo";
  }
}

function pickupMessage(kind: PickupSpawn["kind"]): string {
  const messages: Readonly<Record<PickupSpawn["kind"], string>> = {
    health: "Аптечка подобрана",
    armor: "Бронежилет подобран",
    bullets: "Патроны для пистолета подобраны",
    shells: "Патроны для дробовика подобраны",
    shotgun: "Дробовик «Прорыв-8» подобран",
    rifle: "Автомат «Шквал-30» подобран",
    rifleAmmo: "Магазин для автомата подобран",
  };
  return messages[kind];
}
